import { isAxiosError } from 'axios';
import { createLogger } from 'logging';

import {
    CreateUserRequest,
    ErrorMessage,
    UpdateUserRequest,
    User,
    UserYoutubeChannel,
    UpdateAbuseStatusRequest,
    UserAbuseLog,
} from '@videoblocks/user-service-client';
import { UserApiUpdateAbuseStatusRequest } from '@videoblocks/user-service-client/api/user-api';

import { CreateUserOutcome, CreateUserResult } from './CreateUserResult';
import { userApi } from './userServiceConfig';

const logger = createLogger('UserApi');

const createUser = async (createUserRequest: CreateUserRequest): Promise<CreateUserResult> => {
    try {
        const result = await userApi.createUser({ createUserRequest });
        return { outcome: CreateUserOutcome.UserCreated, user: result.data };
    } catch (error) {
        if (isAxiosError(error)) {
            const { status, data } = error.response || {};
            if (status === 302) {
                return { outcome: CreateUserOutcome.UserAlreadyRegistered };
            }
            if (status === 403 || status === 409) {
                return { outcome: CreateUserOutcome.Error, error: (data as ErrorMessage).message };
            }
        }
        throw error;
    }
};

const getUserBySession = async (sessionToken: string): Promise<User | null> => {
    try {
        const result = await userApi.searchUsers({ session: sessionToken });
        return result.data ?? null;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
    }
    return null;
};

const getUserById = async (userId: number): Promise<User> => {
    const result = await userApi.getUserById({ userId });
    return result.data;
};

const updateUser = async (userId: number, updateData: UpdateUserRequest): Promise<User> => {
    try {
        const result = await userApi.updateUser({
            userId,
            updateUserRequest: updateData,
        });
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            error.message = `Failed to update user: ${error.response?.data?.message || error.message}`;
        }
        throw error;
    }
};

const getUserYoutubeChannel = async (userId: number): Promise<UserYoutubeChannel[]> => {
    const result = await userApi.getUserYoutubeChannels({ userId });
    return result.data;
};

const updateUserYoutubeChannel = async (userId: number, youtubeChannels: string[]): Promise<UserYoutubeChannel[]> => {
    const [youtubeChannel1, youtubeChannel2] = youtubeChannels;
    try {
        const result = await userApi.updateUserYoutubeChannel({
            userId,
            updateUserYoutubeChannelRequest: {
                youtube_channel_id_1: youtubeChannel1,
                youtube_channel_id_2: youtubeChannel2,
            },
        });
        return result.data;
    } catch (err) {
        if (isAxiosError(err)) {
            throw new Error(err.response?.data?.message || err.message);
        }
        throw err;
    }
};

const getAbuseLog = async (userId: number): Promise<UserAbuseLog | null> => {
    try {
        const result = await userApi.getAbuseLogsByUserId({ userId });
        return result.data;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status === 404) {
                logger.info('No abuse status found for the user.', e.message);
                return null;
            }
        }
        throw e;
    }
};

const updateAbuseStatus = async (
    request: UpdateAbuseStatusRequest,
): Promise<{ success: boolean; message?: string }> => {
    try {
        const result = await userApi.updateAbuseStatus({
            updateAbuseStatusRequest: request,
        });
        return {
            success: result.data.success ?? false,
            message: result.data.message,
        };
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status === 400) {
                logger.warn('Error updating abuse status. message: %s', e.message);
                return { success: false, message: e.response?.data?.message || e.message };
            }
        }
        throw e;
    }
};

export const UserApi = {
    createUser,
    getUserBySession,
    getUserById,
    updateUser,
    getUserYoutubeChannel,
    updateUserYoutubeChannel,
    getAbuseLog,
    updateAbuseStatus,
};
